'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Flex,
  HStack,
  IconButton,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ShieldOff } from 'lucide-react'
import dayjs from 'dayjs'
import { useGetDonorDeferralsQuery } from '@/entities/deferral'
import type { IDeferralJournalItem } from '@/entities/deferral'
import { OverrideDeferralModal } from '@/features/override-deferral'
import { DATE_FORMAT } from '@/shared/constants/format'
import { AppCard } from '@/shared'
import {
  DEFERRAL_STATUS_PALETTE,
  deferralSourceKey,
} from '../model/deferral-display'

interface Props {
  donorId: string
  donorName: string
  /** Lifting is the registrar/therapist side's action — off in the lab layout. */
  canOverride?: boolean
}

const formatDate = (value: string | null) =>
  value ? dayjs(value).format(DATE_FORMAT) : '—'

/**
 * Every deferral of the donor — active, expired and lifted, from any blood
 * centre, newest first.
 */
export function DonorDeferralHistoryCard({
  donorId,
  donorName,
  canOverride = false,
}: Props) {
  const { t } = useTranslation()
  const { data: deferrals, isPending } = useGetDonorDeferralsQuery(donorId)
  const [overrideTarget, setOverrideTarget] =
    useState<IDeferralJournalItem | null>(null)

  return (
    <AppCard.Root>
      <AppCard.Body p={5}>
        <Text fontWeight="semibold" fontSize="sm" mb={4}>
          {t('deferrals.history.title')}
        </Text>

        {isPending ? (
          <Skeleton h="80px" borderRadius="md" />
        ) : !deferrals?.length ? (
          <Text fontSize="sm" color="fg.muted">
            {t('deferrals.history.empty')}
          </Text>
        ) : (
          <VStack gap={0} align="stretch" bg="bg.inset" borderRadius="md">
            {deferrals.map((deferral) => {
              const palette = DEFERRAL_STATUS_PALETTE[deferral.status]
              return (
                <Flex
                  key={deferral.id}
                  gap={3}
                  px={3}
                  py={3}
                  align="flex-start"
                  justify="space-between"
                  borderBottomWidth="1px"
                  borderColor="border.muted"
                  _last={{ borderBottom: 'none' }}
                >
                  <Box minW={0}>
                    <HStack gap={2} wrap="wrap" mb={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {deferral.reason_name}
                      </Text>
                      <Badge
                        size="sm"
                        bg={`badge.${palette}.20`}
                        color={`badge.${palette}.100`}
                      >
                        {t(`deferrals.status.${deferral.status.toLowerCase()}`)}
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color="fg.muted">
                      {[
                        t(`deferrals.kind.${deferral.type.toLowerCase()}`),
                        deferral.type === 'PERMANENT'
                          ? t('deferrals.history.since', {
                              date: formatDate(deferral.deferral_from),
                            })
                          : `${formatDate(deferral.deferral_from)} — ${formatDate(deferral.deferral_until)}`,
                        t(deferralSourceKey(deferral.source), {
                          defaultValue: deferral.source,
                        }),
                        deferral.public_id,
                      ].join(' · ')}
                    </Text>
                    {deferral.status === 'LIFTED' && (
                      <Text fontSize="xs" color="fg.muted" mt={1}>
                        {t('deferrals.history.lifted', {
                          date: formatDate(deferral.overridden_at),
                          reason: deferral.override_reason ?? '—',
                        })}
                      </Text>
                    )}
                  </Box>
                  {canOverride && deferral.is_overridable && (
                    <IconButton
                      variant="ghost"
                      size="xs"
                      flexShrink={0}
                      aria-label={t('dashboard.override_deferral.action')}
                      title={t('dashboard.override_deferral.action')}
                      color="fg.muted"
                      _hover={{ color: 'primary.fg', bg: 'bg.muted' }}
                      onClick={() => setOverrideTarget(deferral)}
                    >
                      <ShieldOff size={15} />
                    </IconButton>
                  )}
                </Flex>
              )
            })}
          </VStack>
        )}
      </AppCard.Body>

      {overrideTarget && (
        <OverrideDeferralModal
          open
          donorId={donorId}
          donorName={donorName}
          deferralOptions={[
            { value: overrideTarget.id, label: overrideTarget.reason_name },
          ]}
          onClose={() => setOverrideTarget(null)}
        />
      )}
    </AppCard.Root>
  )
}
